import React, { useEffect, useState } from "react";
import { ArrowLeft, ArrowRight, AlertTriangle, RotateCcw, Trash2, QrCode } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { PageTitle } from "./shared";

const COLUMNS = [
  { value: "queued", label: "PRINT QUEUE", color: "#EC2675" },
  { value: "printing", label: "PRINTING", color: "#FF7A3D" },
  { value: "quality_check", label: "QUALITY CHECK", color: "#6E2470" },
  { value: "ready", label: "READY TO SHIP", color: "#4ECDE9" },
  { value: "completed", label: "COMPLETED", color: "#1F8A50" },
];

const JobCard = ({ job, colIdx, onMove, onFail, onReprint, onDelete, siteUrl }) => {
  const [showQr, setShowQr] = useState(false);
  const tagCount = job.items?.reduce((s, it) => s + it.quantity, 0) || 0;
  const bloomCodes = (job.items || []).flatMap((it) => it.bloom_codes || []);
  return (
    <div className={`bg-cream rounded-2xl p-3.5 border ${job.reprint ? "border-orange/60" : "border-plum-deep/5"}`} data-testid={`print-card-${job.job_number}${job.reprint ? "-reprint" : ""}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="font-semibold text-sm truncate">{job.job_number}</span>
        <span className={`text-[9px] px-2 py-0.5 rounded-full uppercase font-bold tracking-[0.08em] shrink-0 ${job.source === "heritage" ? "bg-forest/15 text-forest" : "bg-plum/15 text-plum"}`}>
          {job.source === "heritage" ? "HERITAGE" : "WORKSHOP"}
        </span>
      </div>
      {job.reprint && <span className="inline-block mt-1 text-[9px] px-2 py-0.5 rounded-full bg-orange/20 text-orange uppercase font-bold">REPRINT</span>}
      <p className="text-xs text-plum-deep/70 truncate mt-1">{job.customer_name}</p>
      <ul className="mt-2 space-y-1">
        {job.items?.slice(0, 3).map((it, i) => (
          <li key={i} className="flex items-center gap-1.5 text-[11px] text-plum-deep/70">
            <span className="w-2.5 h-2.5 rounded-full inline-block shrink-0 border border-plum-deep/15" style={{ background: it.colour_hex }} />
            <span className="flex-1 min-w-0">
              <span className="block truncate">{it.text_line1}{it.size ? ` (${it.size})` : ""}{it.icon?.key ? ` · ${it.icon.key.toUpperCase()} ICON` : ""}</span>
              {it.text_line2 && <span className="block text-[9px] uppercase tracking-[0.1em] text-plum-deep/50 font-semibold truncate" data-testid={`print-item-type-${i}`}>{it.text_line2}</span>}
            </span>
            <span className="ml-auto shrink-0">×{it.quantity}</span>
          </li>
        ))}
        {(job.items?.length || 0) > 3 && <li className="text-[10px] text-plum-deep/40">+{job.items.length - 3} more</li>}
      </ul>
      {bloomCodes.length > 0 && (
        <div className="mt-2">
          <button
            onClick={() => setShowQr(!showQr)}
            data-testid={`print-qr-toggle-${job.job_number}`}
            className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.12em] font-bold text-plum hover:text-magenta"
          >
            <QrCode className="w-3.5 h-3.5" /> {showQr ? "HIDE" : "BLOOM ID"} QR CODES ({bloomCodes.length})
          </button>
          {showQr && (
            <div className="mt-2 grid grid-cols-3 gap-2" data-testid={`print-qr-grid-${job.job_number}`}>
              {bloomCodes.map((code) => (
                <div key={code} className="bg-warmwhite rounded-lg p-1.5 text-center">
                  <QRCodeSVG value={`${siteUrl}/bloom-id?code=${code}`} size={64} className="mx-auto" />
                  <p className="mt-1 text-[8px] font-mono tracking-[0.05em] text-plum-deep/70">{code}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      <p className="text-[10px] text-plum-deep/40 mt-1.5 uppercase tracking-[0.1em]">{tagCount} tags</p>
      <div className="flex items-center justify-between mt-2.5">
        {job.status === "failed" ? (
          <>
            <button onClick={() => onReprint(job)} data-testid={`print-reprint-${job.job_number}`} className="inline-flex items-center gap-1.5 rounded-full bg-magenta text-cream px-3 py-1.5 text-[10px] uppercase font-bold tracking-[0.1em] hover:bg-plum">
              <RotateCcw className="w-3 h-3" /> REPRINT
            </button>
            <button onClick={() => onDelete(job)} className="text-plum-deep/40 hover:text-red-600 p-1" aria-label="Delete job"><Trash2 className="w-3.5 h-3.5" /></button>
          </>
        ) : (
          <>
            <button onClick={() => onMove(job, -1)} disabled={colIdx === 0} data-testid={`print-back-${job.job_number}`} className="p-1.5 rounded-full bg-warmwhite border border-plum-deep/10 disabled:opacity-30 hover:border-magenta" aria-label="Move back">
              <ArrowLeft className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => onFail(job)} data-testid={`print-fail-${job.job_number}`} className="p-1.5 rounded-full text-orange hover:bg-orange/10" title="Mark failed" aria-label="Mark failed">
              <AlertTriangle className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => onMove(job, 1)} disabled={colIdx === COLUMNS.length - 1} data-testid={`print-advance-${job.job_number}`} className="p-1.5 rounded-full text-cream disabled:opacity-30" style={{ background: COLUMNS[colIdx]?.color || "#EC2675" }} aria-label="Advance">
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

const PrintQueue = () => {
  const [jobs, setJobs] = useState(null);
  const [siteUrl, setSiteUrl] = useState("");

  useEffect(() => {
    api.get("/admin/print-jobs").then((r) => setJobs(r.data)).catch(() => setJobs([]));
    api.get("/workshop/config").then((r) => setSiteUrl(r.data.site_url || window.location.origin)).catch(() => setSiteUrl(window.location.origin));
  }, []);

  const setStatus = async (job, status, msg) => {
    await api.patch(`/admin/print-jobs/${job.id}`, { status });
    setJobs((l) => l.map((j) => (j.id === job.id ? { ...j, status } : j)));
    toast.success(msg);
  };

  const move = (job, dir) => {
    const idx = COLUMNS.findIndex((c) => c.value === job.status);
    const next = COLUMNS[idx + dir];
    if (next) setStatus(job, next.value, `${job.job_number} → ${next.label}`);
  };

  const fail = (job) => setStatus(job, "failed", `${job.job_number} marked as failed print`);

  const reprint = async (job) => {
    const { data } = await api.post(`/admin/print-jobs/${job.id}/reprint`);
    setJobs((l) => [data, ...l]);
    toast.success(`${job.job_number} re-queued as a reprint`);
  };

  const remove = async (job) => {
    if (!window.confirm(`Delete failed job ${job.job_number}?`)) return;
    await api.delete(`/admin/print-jobs/${job.id}`);
    setJobs((l) => l.filter((j) => j.id !== job.id));
    toast.success("Job deleted");
  };

  if (jobs === null) return <p className="text-plum-deep/60">Loading print queue…</p>;

  const failed = jobs.filter((j) => j.status === "failed");

  return (
    <div data-testid="console-print-queue">
      <PageTitle kicker="THE ETCHERY · PRODUCTION" title="PRINT QUEUE" />

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
        {COLUMNS.map((col, colIdx) => {
          const colJobs = jobs.filter((j) => j.status === col.value);
          return (
            <div key={col.value} className="bg-warmwhite border border-plum-deep/10 rounded-[1.25rem] p-4 min-h-[220px]" data-testid={`print-col-${col.value}`}>
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-serif-display uppercase text-[13px] tracking-[0.1em] font-semibold" style={{ color: col.color }}>{col.label}</h2>
                <span className="w-6 h-6 rounded-full text-cream text-xs flex items-center justify-center font-bold shrink-0" style={{ background: col.color }}>{colJobs.length}</span>
              </div>
              <div className="space-y-3">
                {colJobs.length === 0 && <p className="text-xs text-plum-deep/40 text-center py-6">Nothing here</p>}
                {colJobs.map((job) => (
                  <JobCard key={job.id} job={job} colIdx={colIdx} onMove={move} onFail={fail} onReprint={reprint} onDelete={remove} siteUrl={siteUrl} />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-6 bg-warmwhite border-2 border-orange/30 rounded-[1.25rem] p-4" data-testid="print-col-failed">
        <div className="flex items-center gap-2 mb-4">
          <AlertTriangle className="w-4 h-4 text-orange" />
          <h2 className="font-serif-display uppercase text-[13px] tracking-[0.1em] font-semibold text-orange">FAILED PRINTS</h2>
          <span className="w-6 h-6 rounded-full bg-orange text-cream text-xs flex items-center justify-center font-bold">{failed.length}</span>
        </div>
        {failed.length === 0 ? (
          <p className="text-xs text-plum-deep/40">No failed prints — the printers are behaving.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5 gap-3">
            {failed.map((job) => (
              <JobCard key={job.id} job={job} colIdx={-1} onMove={move} onFail={fail} onReprint={reprint} onDelete={remove} siteUrl={siteUrl} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PrintQueue;
